import { useState, useEffect, useMemo } from 'react';
import { CalendarDays, List, CheckCircle2, AlertTriangle, Loader2, ChevronRight, X, Users, Eye } from 'lucide-react';
import { peakCall, monthGrids, WEEK_HEAD, fmtNight, fmtLong, fmtStamp, nightLabel, pastDeadline } from '../utils/peakAvailability';

// ── Peak availability — tap the nights you CAN'T work ────────────────────────
// Sorter-portal tab. The office publishes the season's sort nights (peak-availability-sb);
// the sorter taps only the nights they can't work, in a calendar or a list, and sends it.
// Nothing tapped + "I can work every night" is a real answer, not a blank.
// `preview` (a survey object) renders it exactly as a sorter would see it, without fetching
// or saving — the admin's "Preview what sorters see". `teamView` is the read-only team
// calendar, shown behind a switch for the logins the server says may see it (canViewTeam).

const C = {
  card: { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg, 14px)', padding: '20px 18px', marginBottom: 16 },
  title: { fontFamily: 'var(--font-display)', fontSize: '1.15rem', letterSpacing: '0.03em', margin: '0 0 6px' },
  sub: { fontSize: '0.82rem', color: 'var(--hp-dim)', margin: '0 0 12px', lineHeight: 1.55 },
  pill: (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: bg, color: fg }),
  bigBtn: (bg) => ({ width: '100%', padding: '15px', background: bg, color: 'var(--hp-black)', fontWeight: 800, fontSize: '1rem', border: 'none', borderRadius: 'var(--radius-lg, 14px)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'var(--font-body)' }),
  toggle: (on) => ({ flex: 1, padding: '9px 10px', background: on ? 'rgba(201,168,76,0.14)' : 'transparent', color: on ? 'var(--hp-gold)' : 'var(--hp-dim)', border: `1px solid ${on ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'var(--font-body)' }),
};
const RED = 'var(--hp-red, #EF4444)', GREEN = 'var(--hp-green, #22C55E)', ROSE = '#E11D48';

export default function PeakAvailabilityTab({ sorter, onAnswered, preview, teamView }) {
  const [state, setState] = useState(preview ? { survey: preview, mine: null } : null); // { survey, mine, today } | { error }
  const [team, setTeam] = useState(false);
  const [cannot, setCannot] = useState(new Set());
  const [note, setNote] = useState('');
  const [view, setView] = useState('calendar');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (preview) { setState({ survey: preview, mine: null }); return undefined; }
    let alive = true;
    peakCall('getMine').then((r) => {
      if (!alive) return;
      if (!r.success) { setState({ error: r.error || 'Could not load.' }); return; }
      setState(r);
      setCannot(new Set((r.mine && r.mine.cannot) || []));
      setNote((r.mine && r.mine.note) || '');
    }).catch(() => alive && setState({ error: 'Could not reach the server.' }));
    return () => { alive = false; };
  }, [sorter.login, preview]);

  const survey = state && state.survey;
  const nights = useMemo(() => (survey && survey.nights) || [], [survey]);
  const grids = useMemo(() => monthGrids(nights), [nights]);
  const closed = survey && survey.status === 'Closed';
  const late = pastDeadline(survey, state && state.today);

  function toggle(date) {
    if (closed) return;
    setCannot((s) => { const n = new Set(s); n.has(date) ? n.delete(date) : n.add(date); return n; });
    setDirty(true); setMsg(null);
  }

  async function send(allNights) {
    if (preview) { setMsg({ ok: true, text: 'Preview only. Nothing was sent.' }); return; }
    const list = allNights ? [] : [...cannot];
    setBusy(true); setMsg(null);
    try {
      const r = await peakCall('saveMine', { cannot: list, note, name: sorter.name });
      if (!r.success) throw new Error(r.error || 'Could not send.');
      setState((s) => ({ ...s, mine: r.mine }));
      if (allNights) setCannot(new Set());
      setDirty(false);
      setMsg({ ok: true, text: list.length ? `Sent. You can't work ${list.length} night${list.length === 1 ? '' : 's'}. Thanks, we'll roster around them where we can.` : 'Sent. You can work every night. Thank you!' });
      if (onAnswered) onAnswered();
    } catch (e) { setMsg({ ok: false, text: e.message }); } finally { setBusy(false); }
  }

  if (!state) return <div style={{ ...C.card, textAlign: 'center' }}><Loader2 size={20} style={{ animation: 'spin 0.8s linear infinite' }} /></div>;
  if (state.error) return <div style={C.card}><span style={C.pill('rgba(239,68,68,0.12)', RED)}><AlertTriangle size={13} />{state.error}</span></div>;
  if (!survey) return (
    <div style={{ ...C.card, textAlign: 'center', padding: '40px 20px' }}>
      <CalendarDays size={30} color="var(--hp-dim)" />
      <h2 style={{ ...C.title, marginTop: 10 }}>Peak availability</h2>
      <p style={C.sub}>Nothing to fill in yet. The office will put the peak nights here when they're ready.</p>
    </div>
  );

  const mine = state.mine;
  const teamSwitch = state.canViewTeam && teamView && (
    <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
      <button style={C.toggle(!team)} onClick={() => setTeam(false)}><CalendarDays size={14} /> My answer</button>
      <button style={C.toggle(team)} onClick={() => setTeam(true)}><Users size={14} /> Team calendar</button>
    </div>
  );
  if (team && teamSwitch) return <>{teamSwitch}{teamView}</>;
  return (
    <>
      {teamSwitch}
      {preview && <div style={{ ...C.pill('rgba(56,189,248,0.14)', 'var(--hp-sky, #38BDF8)'), marginBottom: 12, padding: '6px 12px' }}><Eye size={13} /> Preview: what a sorter sees on their phone</div>}
      <div style={C.card}>
        <h2 style={C.title}>Peak {survey.season}: nights you can't work</h2>
        {survey.deadline && (
          <div style={{ marginBottom: 12 }}>
            {mine
              ? <span style={C.pill('rgba(34,197,94,0.12)', GREEN)}><CheckCircle2 size={12} /> Sent {fmtStamp(mine.submittedAt)}</span>
              : <span style={C.pill(late ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.14)', late ? RED : 'var(--hp-gold)')}><AlertTriangle size={12} /> {late ? 'Overdue: was due' : 'Due'} {fmtLong(survey.deadline)}</span>}
          </div>
        )}
        <div style={{ fontSize: '0.86rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{survey.message}</div>
      </div>

      <div style={C.card}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button style={C.toggle(view === 'calendar')} onClick={() => setView('calendar')}><CalendarDays size={14} /> Calendar</button>
          <button style={C.toggle(view === 'list')} onClick={() => setView('list')}><List size={14} /> List</button>
        </div>
        <p style={{ ...C.sub, marginBottom: 14 }}>
          {closed ? 'This has closed. Tell your team leader about any change.' : <>Tap a night to mark it <strong style={{ color: RED }}>can't work</strong>. Tap again to undo. <span style={{ color: ROSE, fontWeight: 700 }}>Sat</span> = a Saturday run.</>}
        </p>

        {view === 'calendar' ? grids.map((g) => (
          <div key={g.key} style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 800, fontSize: '0.92rem', marginBottom: 8 }}>{g.title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
              {WEEK_HEAD.map((h) => <div key={h} style={{ fontSize: '0.66rem', fontWeight: 700, color: 'var(--hp-dim)', textAlign: 'center', paddingBottom: 2 }}>{h}</div>)}
              {g.cells.map((c, i) => {
                if (!c) return <div key={i} />;
                const n = c.night;
                if (!n) return <div key={i} style={{ aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', color: 'var(--hp-dim)', opacity: 0.35 }}>{c.day}</div>;
                const no = cannot.has(n.date);
                return (
                  <button key={i} onClick={() => toggle(n.date)} disabled={closed} title={nightLabel(n)}
                    style={{ aspectRatio: '1', borderRadius: 8, border: `1.5px solid ${no ? RED : n.peak ? ROSE : 'var(--hp-border)'}`, background: no ? 'rgba(239,68,68,0.22)' : 'rgba(34,197,94,0.08)', color: no ? RED : 'var(--hp-text)', fontWeight: 800, fontSize: '0.86rem', cursor: closed ? 'default' : 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, padding: 0, fontFamily: 'var(--font-body)' }}>
                    {c.day}
                    {no ? <X size={11} /> : n.peak ? <span style={{ fontSize: '0.56rem', color: ROSE, fontWeight: 800 }}>Sat</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        )) : (
          <div>
            {nights.map((n) => {
              const no = cannot.has(n.date);
              return (
                <button key={n.date} onClick={() => toggle(n.date)} disabled={closed}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px', marginBottom: 6, borderRadius: 10, border: `1px solid ${no ? RED : 'var(--hp-border)'}`, background: no ? 'rgba(239,68,68,0.14)' : 'transparent', color: 'var(--hp-text)', cursor: closed ? 'default' : 'pointer', textAlign: 'left', fontFamily: 'var(--font-body)' }}>
                  <span style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${no ? RED : 'var(--hp-border)'}`, background: no ? RED : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{no && <X size={13} color="#fff" />}</span>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: '0.9rem' }}>{fmtNight(n.date)} night</span>
                  {n.peak && <span style={C.pill('rgba(225,29,72,0.14)', ROSE)}>Saturday run</span>}
                  <span style={{ fontSize: '0.76rem', fontWeight: 700, color: no ? RED : GREEN, minWidth: 64, textAlign: 'right' }}>{no ? "Can't work" : 'Available'}</span>
                </button>
              );
            })}
          </div>
        )}

        {!closed && (
          <>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '14px 0 6px' }}>Anything we should know? (optional)</label>
            <input value={note} onChange={(e) => { setNote(e.target.value); setDirty(true); }} placeholder="e.g. away 12–20 Dec, back for Saturday runs"
              style={{ width: '100%', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', fontSize: '1rem', padding: '12px 14px', marginBottom: 14, boxSizing: 'border-box', fontFamily: 'var(--font-body)' }} />
            {msg && <div style={{ ...C.pill(msg.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', msg.ok ? GREEN : RED), marginBottom: 12, padding: '8px 12px', borderRadius: 10 }}>{msg.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}{msg.text}</div>}
            {cannot.size > 0 ? (
              <button style={C.bigBtn('var(--hp-gold)')} disabled={busy} onClick={() => send(false)}>
                {busy ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : <>Send: I can't work {cannot.size} night{cannot.size === 1 ? '' : 's'} <ChevronRight size={18} /></>}
              </button>
            ) : (
              <button style={C.bigBtn(GREEN)} disabled={busy} onClick={() => send(true)}>
                {busy ? <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> : <><CheckCircle2 size={18} /> I can work every night</>}
              </button>
            )}
            {mine && dirty && <p style={{ ...C.sub, marginTop: 10, marginBottom: 0, color: 'var(--hp-gold)' }}>You've made changes. Send again to update your answer.</p>}
          </>
        )}
      </div>
    </>
  );
}

// Banner for the other tabs until they've answered. Hidden when nothing is published.
export function PeakAvailabilityBanner({ login, onOpen, refreshKey }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let alive = true;
    peakCall('getMine').then((r) => { if (alive && r && r.success) setSt(r); }).catch(() => {});
    return () => { alive = false; };
  }, [login, refreshKey]);
  if (!st || !st.survey || st.mine || st.survey.status === 'Closed') return null;
  const late = pastDeadline(st.survey, st.today);
  return (
    <div style={{ ...C.card, borderColor: late ? RED : 'var(--hp-amber, #F59E0B)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <CalendarDays size={20} color={late ? RED : 'var(--hp-amber, #F59E0B)'} />
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>Peak roster: tell us the nights you can't work</div>
        <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>{st.survey.deadline ? `${late ? 'It was due' : 'Due'} ${fmtLong(st.survey.deadline)}. ` : ''}Takes a minute. Tap only the nights you can't do.</div>
      </div>
      <button style={{ ...C.bigBtn('var(--hp-gold)'), width: 'auto', padding: '12px 16px' }} onClick={onOpen}>Fill it in</button>
    </div>
  );
}

// Whether the tab should carry a red dot (published, open, not yet answered).
export function usePeakOutstanding(login, refreshKey) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!login) { setN(0); return undefined; }
    let alive = true;
    peakCall('getMine').then((r) => { if (alive && r && r.success) setN(r.survey && !r.mine && r.survey.status !== 'Closed' ? 1 : 0); }).catch(() => {});
    return () => { alive = false; };
  }, [login, refreshKey]);
  return n;
}
